import { ReactElement, useEffect } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { userHasRoleAccess } from '../../helpers/AccessManagementHelpers';
import { getToken, routeToLogin, userIsAuthenticated } from '../../helpers/AuthHelpers';
import { ROLE_NAME, User } from '../../types/User';
import ErrorPage from '../../views/ErrorPage';
import Layout from '../Layout';
import Loader from '../Loader';

export interface AccessCheck {
    canAccess: boolean;
    isLoading: boolean;
}

interface Props {
    renderComponent: (user: User) => ReactElement | null;
    allowedRoles: readonly ROLE_NAME[];
    accessCheck?: AccessCheck;
    currentUser?: User;
    isLoadingUser?: boolean;
}

const PrivateRoute = ({
    renderComponent,
    allowedRoles,
    accessCheck,
    currentUser,
    isLoadingUser
}: Props): ReactElement | null => {
    const navigate = useNavigate();

    useRedirectIfNotAuthenticated();

    const hasAccess = userHasRoleAccess(currentUser, allowedRoles) || (accessCheck?.canAccess ?? false);
    const isLoading = isLoadingUser || accessCheck?.isLoading === true;
    useRedirectIfNoAccess(currentUser, hasAccess, isLoading, navigate);

    if (isLoading) {
        return <Layout mainContent={<Loader size={20} />} />;
    }

    if (!currentUser) {
        return (
            <Layout
                mainContent={
                    <ErrorPage
                        errHeadline="User Not Found"
                        errMessage="We couldn’t retrieve your user information. Please try refreshing the page or contact support."
                    />
                }
                currentUser={currentUser}
                loadingUser={isLoadingUser}
            />
        );
    }

    return (
        <Layout
            mainContent={hasAccess ? renderComponent(currentUser) : null}
            currentUser={currentUser}
            loadingUser={isLoadingUser}
        />
    );
};

const useRedirectIfNotAuthenticated = () => {
    useEffect(() => {
        if (!userIsAuthenticated(getToken())) {
            routeToLogin();
        }
    }, []);
};

const useRedirectIfNoAccess = (
    currentUser: User | undefined,
    hasAccess: boolean,
    isLoading: boolean,
    navigate: NavigateFunction
) => {
    useEffect(() => {
        if (!isLoading && currentUser && !hasAccess) {
            navigate('/no-access');
        }
    }, [currentUser, hasAccess, isLoading]);
};

export default PrivateRoute;
